// TODO: consolidate with better thought out and carefully tested sorting functions here:
// https://github.com/ChatableAI/chatable-ux/blob/master/src/common/helpers/utils/sorting.js#L13
import React from 'react';
import { compose, withHandlers } from 'recompose';
import { SortableContainer } from '../../../../../../common/lib/react-sortable-hoc';
import { FormEntry } from '@mm/state/model/FormsModel';
import WidgetModel, { ListAndGalleryEntry } from '@mm/state/model/WidgetModel';

export type SortableEntry = FormEntry | ListAndGalleryEntry;

type SetItemsContainerRef = () => (ref: HTMLElement) => void;
type GetItemsContainerRef = () => () => HTMLElement;
type SetItemRef = () => (ref: HTMLElement) => void;
type GetItemsRefs = () => () => HTMLElement[];
type SetSortedEntries = () => (entries: FormEntry[]) => void;
type GetSortedEntries = () => () => SortableEntry[];

type PropsFromRefHandlers = {
  setItemsContainerRef: ReturnType<SetItemsContainerRef>;
  getItemsContainerRef: ReturnType<GetItemsContainerRef>;
  setItemRef: ReturnType<SetItemRef>;
  getItemsRefs: ReturnType<GetItemsRefs>;
  setSortedEntries: ReturnType<SetSortedEntries>;
  getSortedEntries: ReturnType<GetSortedEntries>;
};

export type OnDropEntry = ({
  onUpdateEntry: Function,
  shouldChangeSortOrder: ShouldChangeSortOrder,
  widget: WidgetModel,
}) => (
  sourceEntry: SortableEntry,
  targetEntry: SortableEntry,
  top: boolean,
) => void;

export type OnSortEndInput = {
  getSortedEntries: ReturnType<GetSortedEntries>;
  onDropEntry: ReturnType<OnDropEntry>;
};

export type OnSortEndInnerInput = { oldIndex: number; newIndex: number };

export type OnSortEnd = ({
  getSortedEntries,
  onDropEntry,
}: OnSortEndInput) => ({ oldIndex, newIndex }: OnSortEndInnerInput) => void;

export const onSortEnd: OnSortEnd =
  ({ getSortedEntries, onDropEntry }) =>
  ({ oldIndex, newIndex }) => {
    const sortedEntries = getSortedEntries();
    onDropEntry(
      sortedEntries[oldIndex],
      sortedEntries[newIndex],
      newIndex < oldIndex,
    );
  };

export type ShouldChangeSortOrder = (
  newOrder: number,
  oldOrder: number,
  entryOrder: number,
  gettingUpper: boolean,
) => boolean;

const shouldChangeSortOrder: ShouldChangeSortOrder = (
  newOrder,
  oldOrder,
  entryOrder,
  gettingUpper,
) =>
  (entryOrder > newOrder === gettingUpper || entryOrder === newOrder) &&
  !(entryOrder > oldOrder === gettingUpper);

// TODO: check if moving entries out is doable, to make it more general.
const onDropEntry: OnDropEntry = props => (sourceEntry, targetEntry, top) => {
  const {
    onUpdateEntry,
    widget: {
      config: { entries },
    },
  } = props;
  if (sourceEntry.sortOrder === targetEntry.sortOrder) {
    return;
  }
  const gettingUpper = sourceEntry.sortOrder > targetEntry.sortOrder;
  let sortOrderCorrection = -1;
  if (gettingUpper) sortOrderCorrection += 1;
  const oldSortOrder = sourceEntry.sortOrder;
  const newSortOrder =
    targetEntry.sortOrder +
    (top ? sortOrderCorrection : sortOrderCorrection + 1);
  if (newSortOrder === sourceEntry.sortOrder) {
    return;
  }

  entries.forEach(entry => {
    if (
      shouldChangeSortOrder(
        newSortOrder,
        oldSortOrder,
        entry.sortOrder,
        gettingUpper,
      ) &&
      entry.id !== sourceEntry.id
    ) {
      // TODO: add batch updates instead of sequential.
      // Motivation - ensure consistency and minimize networks requests.
      // Sample implementation done for Page here.
      // src/editor/features/build/components/PageOverviewPane/PageOverviewPane.js#L50
      // Not doing it here now since it is a little bit less critical.
      onUpdateEntry({
        ...entry,
        sortOrder: entry.sortOrder + (gettingUpper ? 1 : -1),
      });
    }
  });

  onUpdateEntry({
    ...sourceEntry,
    sortOrder: newSortOrder,
  });
};

export type SortingInnerProps = PropsFromRefHandlers & {
  onDropEntry: OnDropEntry;
  shouldChangeSortOrder: ShouldChangeSortOrder;
  onSortEnd: OnSortEnd;
};

type OuterProps = {
  onUpdateEntry: Function;
  widget: WidgetModel;
};

// TODO: deprecate in favor of sortingHook when time allows.
export const withSorting = compose<SortingInnerProps, OuterProps>(
  withHandlers(() => {
    let itemsContainerRef = null;
    const itemsRefs = [];
    let sortedEntries = [];

    return {
      setItemsContainerRef: () => ref => {
        itemsContainerRef = ref;
      },
      getItemsContainerRef: () => () => itemsContainerRef,
      // TODO: Fix strict null error
      // @ts-ignore
      setItemRef: () => ref => ref && itemsRefs.push(ref),
      getItemsRefs: () => () => itemsRefs,
      setSortedEntries: () => entries => {
        sortedEntries = entries.sort((a, b) => a.sortOrder - b.sortOrder);
      },
      getSortedEntries: () => () => sortedEntries,
    };
  }),
  withHandlers({
    onDropEntry,
  }),
  withHandlers({
    onSortEnd,
  }),
);

type ItemsContainerProps = {
  className?: string;
  children: any;
  containerRef: any;
  lockAxis?: string;
  useDragHandle?: boolean;
  lockOffset?: string;
  lockToContainerEdges?: boolean;
  onSortEnd: (onSortEnd) => void;
};
export type ItemsContainerType = React.StatelessComponent<ItemsContainerProps>;

export const ItemsContainer: ItemsContainerType = SortableContainer(
  ({ children, containerRef, ...props }) => (
    <div {...props} ref={containerRef}>
      {children}
    </div>
  ),
);
